<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { formatDate, parseISODate } from '@/utils/dateUtils';
import { dateFormats, type SupportedLocale } from '@/plugins/i18n';

const props = defineProps<{
  name: string;
  modelValue: string | undefined;
  label?: string;
  disabled?: boolean;
}>();

const { t, locale } = useI18n();

const internalValue = ref<Date | null>(null); // Localized date as Date object

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | undefined): void;
  (e: 'blur'): void;
}>();

// Dynamic Date Format based on locale
const dynamicDateFormat = computed(
  () => dateFormats[locale.value as SupportedLocale] || 'yyyy-MM-dd',
);

// Computed property to display formatted date as text when disabled
const formattedDate = computed(() => {
  return internalValue.value ? formatDate(internalValue.value, locale.value) : '';
});

const onDateChange = (newDate: Date | Date[] | (Date | null)[] | null | undefined) => {
  if (newDate instanceof Date) {
    // Add two hours to the date to adjust for timezone offset
    const adjustedDate = new Date(new Date(newDate).getTime() + 2 * 60 * 60 * 1000);

    const isoDate = adjustedDate.toISOString();
    emit('update:modelValue', isoDate);
  } else {
    emit('update:modelValue', undefined);
  }

  void nextTick(() => emit('blur'));
};

// Watch for changes in modelValue and update internalValue accordingly
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      internalValue.value = parseISODate(newValue); // Convert ISO string to Date
    } else {
      internalValue.value = null;
    }
  },
  { immediate: true },
);

// Watch for changes in locale.value and update date format and internalValue
watch(
  () => locale.value,
  () => {
    // Update internalValue to reflect the new locale format
    if (props.modelValue) {
      internalValue.value = parseISODate(props.modelValue);
    }
  },
  { immediate: true },
);

onMounted(() => {
  if (props.modelValue) {
    internalValue.value = parseISODate(props.modelValue);
  }
});
</script>

<template>
  <div class="form-group">
    <label :for="name">{{
      props.label ? t(`labels.${props.label}`) : t(`labels.${props.name}`)
    }}</label>

    <DatePicker
      v-if="!disabled"
      :id="name"
      :key="locale"
      v-model="internalValue"
      updateModelType="date"
      fluid
      iconDisplay="input"
      inputId="icondisplay"
      :disabled="disabled"
      :locale="locale"
      :dateFormat="dynamicDateFormat"
      @update:model-value="onDateChange"
    />

    <InputText v-else :id="name" type="text" :value="formattedDate" disabled class="form-control" />
  </div>
</template>
